import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import "./Profile.css";
import defaultProfile from "../../assets/default.png"; // fallback image

const USERS_BASE_URL = import.meta.env.VITE_USERS_BASE_URL;

function Profile() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    country_code: '',
    image: null
  });
  const navigate = useNavigate();

  const handleSignOut = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    window.location.href = "/login";
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`${USERS_BASE_URL}/profile/`, {
            headers: {
            'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
          }
        });

        if (response.status === 401) {
          navigate("/login");
          return;
        }

        const data = await response.json();
        setUser(data);
        setFormData({
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          email: data.email || '',
          phone_number: data.phone_number || '',
          country_code: data.country_code || '',
          image: null
        });
      } catch (error) {
        console.error("Failed to fetch profile:", error);
        setError("Failed to fetch profile");
        if (error.status === 401) {
          localStorage.removeItem("accessToken");
          localStorage.removeItem("refreshToken");
          window.location.href = "/login";
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({
        ...prev,
        image: e.target.files[0]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem("accessToken");
    if (!token) return;

    try {
      const formDataToSend = new FormData();
      
      // Add all text fields
      formDataToSend.append('first_name', formData.first_name || '');
      formDataToSend.append('last_name', formData.last_name || '');
      formDataToSend.append('email', formData.email || '');
      formDataToSend.append('phone_number', formData.phone_number || '');
      formDataToSend.append('country_code', formData.country_code || '');
      
      // Only append image if it's a new one
      if (formData.image) {
        formDataToSend.append('image', formData.image);
      }

      const response = await fetch(
        `${USERS_BASE_URL}/profile/`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formDataToSend
        }
      );

      if (response.status === 401) {
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        window.location.href = "/login";
        return;
      }

      const data = await response.json();
      setUser(data);
      setIsEditing(false);
      setError(null);
    } catch (error) {
      console.error("Failed to update profile:", error);
      setError("Failed to update profile");
    }
  };

  if (loading) return <p>Loading profile...</p>;

  if (!user) return <p>User not logged in.</p>;

  const imageUrl = user.image
    ? user.image.startsWith('http') ? user.image : `${USERS_BASE_URL}${user.image}`
    : defaultProfile;

  return (
    <div className="profile-card">
      <div className="profile-header">
        <h2>Profile</h2>
        <button 
          className="edit-button" 
          onClick={() => setIsEditing(!isEditing)}
        >
          {isEditing ? 'Cancel' : 'Edit Profile'}
        </button>
      </div>

      {isEditing ? (
        <form onSubmit={handleSubmit} className="profile-form">
          <div className="profile-image-container">
            <img src={imageUrl} alt="Profile" className="profile-image" />
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="image-upload"
            />
          </div>

          <div className="form-group">
            <label>First Name:</label>
            <input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Last Name:</label>
            <input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Email:</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
            />
          </div>

          <div className="form-group">
            <label>Country Code:</label>
            <input
              type="text"
              name="country_code"
              value={formData.country_code}
              onChange={handleInputChange}
              placeholder="+1"
            />
          </div>

          <div className="form-group">
            <label>Phone Number:</label>
            <input
              type="tel"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
            />
          </div>

          {error && <p className="error-message">{error}</p>}

          <div className="button-group">
            <button type="submit" className="save-button">Save Changes</button>
            <button type="button" className="signout-button" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </form>
      ) : (
        <>
          <img src={imageUrl} alt="Profile" className="profile-image" />
          <p>
            <strong>Name:</strong> {`${user.first_name} ${user.last_name}`.trim()}
          </p>
          <p>
            <strong>Email:</strong> {user.email}
          </p>
          <p>
            <strong>Phone Number:</strong> {user.country_code && user.phone_number
              ? `${user.country_code}${user.phone_number}`
              : "Not provided"}
          </p>

          {error && <p className="error-message">{error}</p>}

          <button className="signout-button" onClick={handleSignOut}>
            Sign Out
          </button>
        </>
      )}
    </div>
  );
}

export default Profile;
